// OS notifications shared by the chat-message and announcement notifiers.
//
// Both builds use the renderer's own HTML5 Notification API — Electron's main process only
// auto-grants the permission (src/main.js installMediaPermissionHandlers), so the one piece of
// IPC here is the optional focusWindow raise on click. A plain browser tab (web mode) differs in
// ways this module absorbs so the notifiers don't have to:
//   - the permission prompt needs a user click: Firefox rejects and Safari ignores a request
//     made from a WebSocket handler (and Chrome may quiet-block the site), so web mode NEVER
//     prompts from a notify path — only the explicit Enable UI calls
//     requestNotificationPermissionFromUserGesture();
//   - a duplicated tab shares the session and runs its own socket, so every web notification
//     carries a per-user tag that collapses the copies (Chromium/Firefox; Safari ignores tags);
//   - a browser shows no app icon when `icon` is omitted, so the Healo logo is the fallback;
//   - a click has to close the notification itself.
// Electron keeps exactly its previous options and lazy prompt.

import healoLogoUrl from '@/assets/healo-logo.png';
import { getAuthUser } from '@/lib/api/token-store';
import { isWebMode } from '@/lib/config/web-mode';

const LOG_PREFIX = '[SystemNotifications]';

/**
 * @typedef {'unsupported' | 'insecure' | 'default' | 'granted' | 'denied'} NotificationPermissionState
 */

/**
 * This device's notification permission, read live (never cached: a site-settings change or a
 * test double swaps the answer under us). A primitive, so it doubles as a useSyncExternalStore
 * snapshot.
 * @returns {NotificationPermissionState}
 */
export function getNotificationPermissionState() {
  if (typeof window === 'undefined') {
    return 'unsupported';
  }
  // Before the API check: Blink keeps `Notification` on insecure origins but reports "denied",
  // which would send the user to site settings for what is really a plain-http problem.
  if (window.isSecureContext === false) {
    return 'insecure';
  }
  if (typeof Notification === 'undefined') {
    return 'unsupported';
  }
  const permission = Notification.permission;
  return permission === 'granted' || permission === 'denied' ? permission : 'default';
}

const permissionListeners = new Set();
let permissionWatchInstalled = false;
let lastEmittedState = null;

function emitPermissionIfChanged() {
  const state = getNotificationPermissionState();
  if (state === lastEmittedState) {
    return;
  }
  lastEmittedState = state;
  for (const listener of [...permissionListeners]) {
    try {
      listener(state);
    } catch (error) {
      console.warn(LOG_PREFIX, 'permission listener failed', error);
    }
  }
}

function installPermissionWatch() {
  if (permissionWatchInstalled || typeof window === 'undefined') {
    return;
  }
  permissionWatchInstalled = true;
  lastEmittedState = getNotificationPermissionState();
  // Site-settings changes arrive live where the Permissions API supports the descriptor. It names
  // the default state "prompt", so it is only a trigger — Notification.permission stays the truth.
  try {
    const query = typeof navigator === 'undefined' ? null : navigator.permissions?.query?.({ name: 'notifications' });
    query?.then?.(
      (status) => status?.addEventListener?.('change', emitPermissionIfChanged),
      () => {},
    );
  } catch (_error) {
    // Browsers without the notifications descriptor throw synchronously; focus covers them.
  }
  // Everything else: the user changes site settings in another surface, then comes back.
  window.addEventListener?.('focus', emitPermissionIfChanged);
  if (typeof document !== 'undefined') {
    document.addEventListener?.('visibilitychange', emitPermissionIfChanged);
  }
}

/**
 * Calls `listener(state)` whenever the permission state changes. Returns an unsubscribe.
 * @param {(state: NotificationPermissionState) => void} listener
 */
export function subscribeNotificationPermission(listener) {
  if (typeof listener !== 'function') {
    return () => {};
  }
  installPermissionWatch();
  permissionListeners.add(listener);
  return () => {
    permissionListeners.delete(listener);
  };
}

/**
 * Shows the browser's permission prompt. MUST be called synchronously from a click handler —
 * Firefox and Safari only honor a request made during the click's user activation, so nothing
 * may be awaited before it. Resolves with the resulting state (still 'default' when the prompt
 * was dismissed or refused); a no-op unless the state is 'default'.
 * @returns {Promise<NotificationPermissionState>}
 */
export function requestNotificationPermissionFromUserGesture() {
  const current = getNotificationPermissionState();
  if (current !== 'default') {
    return Promise.resolve(current);
  }
  return new Promise((resolve) => {
    let settled = false;
    const settle = () => {
      if (settled) {
        return;
      }
      settled = true;
      emitPermissionIfChanged();
      resolve(getNotificationPermissionState());
    };
    try {
      // The callback serves Safari < 15, which has no promise form; Chrome both calls it and
      // resolves, hence the settle-once guard. Firefox rejects a request it refuses.
      const maybePromise = Notification.requestPermission(settle);
      maybePromise?.then?.(settle, settle);
    } catch (_error) {
      settle();
    }
  });
}

let permissionRequested = false;

/**
 * Whether a notification may be shown right now.
 *
 * Electron: asks once per page load (the main process auto-grants, so this resolves at once).
 * Web mode: never asks — this runs for incoming messages, outside any click, where the request
 * is refused; it just reports whether the user already enabled notifications (and stays quiet,
 * since it runs per message).
 * @returns {Promise<boolean>}
 */
export async function ensureNotificationPermission() {
  if (isWebMode()) {
    return getNotificationPermissionState() === 'granted';
  }
  if (typeof window === 'undefined' || typeof Notification === 'undefined') {
    console.warn(LOG_PREFIX, 'Notification API unavailable (not a browser context or missing API)');
    return false;
  }
  console.debug(LOG_PREFIX, 'Notification.permission =', Notification.permission);
  if (Notification.permission === 'granted') {
    return true;
  }
  if (Notification.permission === 'denied') {
    console.warn(LOG_PREFIX, 'Notifications blocked by user or OS — check system settings / site permissions');
    return false;
  }
  if (permissionRequested) {
    const granted = Notification.permission === 'granted';
    if (!granted) {
      console.warn(LOG_PREFIX, 'Permission still not granted after earlier prompt:', Notification.permission);
    }
    return granted;
  }
  permissionRequested = true;
  const result = await Notification.requestPermission();
  console.info(LOG_PREFIX, 'Notification.requestPermission() result =', result);
  return result === 'granted';
}

/**
 * Shows one OS notification; call only after ensureNotificationPermission() resolved true.
 * Clicking it raises the window (Electron) / focuses the tab (web), then runs `onClick`.
 *
 * @param {object} params
 * @param {string} params.title
 * @param {string} [params.body]
 * @param {string} [params.icon] absolute URL; omitted when empty (web mode: the Healo logo)
 * @param {string} [params.tag] per-event dedup key (web mode only, prefixed with the user id so
 *   two users in two tabs never replace each other's notifications)
 * @param {() => void} [params.onClick]
 * @returns {Notification | null} null when the platform refused to construct it
 */
export function showSystemNotification({ title, body = '', icon = '', tag = '', onClick } = {}) {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') {
    return null;
  }
  const web = isWebMode();
  const options = { body, silent: false };
  const iconUrl = String(icon || '').trim() || (web ? healoLogoUrl : '');
  if (iconUrl) {
    options.icon = iconUrl;
  }
  const tagKey = String(tag || '').trim();
  if (web && tagKey) {
    // Same origin + same tag = the same notification, replaced silently (renotify stays unset:
    // Firefox/Safari lack it and Chrome throws when it is set without a tag).
    options.tag = `healo:${String(getAuthUser()?.id || '').trim()}:${tagKey}`;
  }
  let notification;
  try {
    notification = new Notification(String(title || ''), options);
  } catch (error) {
    // e.g. Android Chrome, which only allows service-worker notifications.
    console.warn(LOG_PREFIX, 'Notification constructor failed', error);
    return null;
  }
  notification.addEventListener('click', () => {
    try {
      window.focus?.();
      // Electron: raise/restore the window from the main process (browser no-op).
      window.electronAPI?.focusWindow?.();
      if (web) {
        // Browsers (Chrome/Firefox on Windows especially) leave a clicked notification up.
        notification.close?.();
      }
      onClick?.();
    } catch (_error) {
      // ignore
    }
  });
  return notification;
}
